/*
This table has two modes:
naive - contains only shared columns across all entity types (name, risk, source, included in CAs, last seen...)
entity type specific - contains columns specific to the entity type (e.g. OS, Type, Expiration Date...)
*/

import { Box, Typography } from "@mui/material";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useMSSP from "../../../../api/hooks/useMSSP";
import LoadingIcon from "../../../../components/LoadingIcon";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { QueryParam } from "../../../../utils/constants";
import { useLocalTime } from "../../../../utils/timestamp";
import getTableColumns from "./columns";
import { formatFieldHeaderName } from "./fieldUtils";

export default function EntitiesTable({
  entityType,
  entitiesAreLoading,
  entitiesAreFetching,
  fetchedEntities,
  filteredEntities,
  setCheckedEntityIDs,
  assets,
  gridRef,
  visibleColumns = null,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  isLoading,
  ...props
}) {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedTenantIds, hasMultipleSelectedTenants } = useMSSP();
  const [sortModel, setSortModel] = useState([
    { field: "risk", sort: "desc" },
    { field: "display_name", sort: "asc" },
  ]);
  const onCellClick = (params, _) => {
    if (params.field === "__check__") {
      trackEvent("user toggled an entity row checkbox in the entities table");
      return;
    }
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.set(
      QueryParam.entity,
      encodeURIComponent(params.row.unique_id)
    );
    setSearchParams(updatedSearchParams);
  };

  const showLocalTime = useLocalTime();
  const baseColumns = getTableColumns(
    entityType,
    fetchedEntities,
    assets,
    colors,
    showLocalTime,
    selectedTenantIds,
    hasMultipleSelectedTenants
  );

  const customColumns =
    visibleColumns && visibleColumns.length > 0
      ? visibleColumns.map((fieldName) => ({
          field: fieldName,
          headerName: formatFieldHeaderName(fieldName),
          minWidth: 150,
          flex: 0.5,
          sortable: true,
          resizable: true,
          renderCell: (params) => {
            const value = params.value;

            if (value === null || value === undefined) {
              return "-";
            }

            if (Array.isArray(value)) {
              return value.length > 0 ? value.join(", ") : "-";
            }

            if (typeof value === "object") {
              try {
                return JSON.stringify(value);
              } catch {
                return String(value);
              }
            }

            return String(value);
          },
        }))
      : [];

  const columns = [...baseColumns, ...customColumns];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "4px",
          height: "16px",
          padding: "0px 8px",
        }}
        visibility={
          entitiesAreFetching && !entitiesAreLoading ? "visible" : "hidden"
        }
      >
        <LoadingIcon type="circular" size="14px" />
        <Typography variant="caption" fontSize="14px" color="text.secondary">
          Refreshing data
        </Typography>
      </Box>

      <StyledDataGrid
        columns={columns}
        apiRef={gridRef}
        rows={filteredEntities}
        loading={entitiesAreLoading}
        onCellClick={onCellClick}
        checkboxSelection
        onRowSelectionModelChange={(checkedEntityIDs) => {
          setCheckedEntityIDs(checkedEntityIDs);
        }}
        getRowId={(row) => row.entity_id + "-" + row.customer_id}
        initialState={{
          sorting: {
            sortModel: sortModel,
          },
        }}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        pageSizeOptionsOverride={[10, 20, 50]}
        columnVisibilityModel={
          columnVisibilityModel || { local_user_admin: false }
        }
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        {...props}
      />
    </Box>
  );
}
